import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { API_CONFIG } from '@/config/env';
import i18n from '@/providers/i18nProvider';
import { asHttpError } from '@/utils/apiError';
import { getAccessToken, clearTokens } from '@/utils/storage';

/** Admin ``revoke-all`` calls LCPS per licence sequentially; default API timeout is too low. */
const ISSUED_LICENCES_REVOKE_ALL_TIMEOUT_MS = 300_000;

// Create Axios instance
export const axiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (i18n.language) {
      config.headers['Accept-Language'] = i18n.language;
    }
    const url = config.url ?? '';
    if (url.includes('issued-licences') && url.includes('revoke-all')) {
      config.timeout = Math.max(
        config.timeout ?? API_CONFIG.timeout,
        ISSUED_LICENCES_REVOKE_ALL_TIMEOUT_MS
      );
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear tokens and redirect to login
      clearTokens();
      window.location.href = '/login';
    }

    return Promise.reject(asHttpError(error));
  }
);

// Orval custom mutator for generated API client
export const customAxiosInstance = <T>(
  config: AxiosRequestConfig
): Promise<T> => {
  return axiosInstance.request(config).then(({ data }) => data);
};
